import type { ContentDraft } from '../../../lib/services/agents/contentDraftApi';
import type { ContentStudioDraft } from '../ContentStudioPage';

/** A featured product snapshotted with the draft (`productsSnapshot` JSON). */
export interface DraftProduct {
  name: string;
  price?: string | number;
  image_url?: string;
  url?: string;
}

/** Parse the draft's `productsSnapshot` (a JSON string) into a product list.
 *  Tolerates null / malformed JSON / non-array shapes — returns [] on anything odd. */
export function parseDraftProducts(snapshot?: string | null): DraftProduct[] {
  if (!snapshot) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(snapshot);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
    .filter(p => typeof p.name === 'string' && (p.name as string).trim())
    .map(p => ({
      name: (p.name as string).trim(),
      price:
        typeof p.price === 'number' || typeof p.price === 'string'
          ? (p.price as string | number)
          : undefined,
      image_url: typeof p.image_url === 'string' ? p.image_url : undefined,
      url: typeof p.url === 'string' ? p.url : undefined,
    }));
}

/** Durable image handles persisted with the draft (`media` JSON). Same shape
 *  CREATE_CONTENT_DRAFT produces: {"items":[{"image_key","role"}]}. Tolerates
 *  null / malformed JSON / odd shapes — returns [] on anything unexpected. */
export function parseDraftMedia(
  media?: string | null
): { image_key?: string; image_url?: string; role?: string }[] {
  if (!media) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(media);
  } catch {
    return [];
  }
  const items = Array.isArray(raw) ? raw : (raw as { items?: unknown })?.items;
  if (!Array.isArray(items)) return [];
  return items
    .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object')
    .map(m => ({
      image_key: typeof m.image_key === 'string' ? m.image_key.trim() || undefined : undefined,
      // An editor-replaced cover has no usable durable key (its upload lands under a nested
      // prefix the backend refuses to sign), so it is persisted as a URL instead.
      image_url: typeof m.image_url === 'string' ? m.image_url.trim() || undefined : undefined,
      role: typeof m.role === 'string' ? m.role : undefined,
    }))
    .filter(m => m.image_key || m.image_url);
}

/**
 * The picture that represents a draft.
 *
 * `imageUrl` first: that is the draft's OWN cover, resolved server-side from the durable
 * `image_key` Maya stored in `media` — the same key the publish path hands Shopify, and the
 * same one the Material Hub renders. The fallbacks stay for drafts that carry no key — older
 * rows, and pieces whose hero really is a product shot pulled from the store:
 *  1. the first `![alt](url)` in the markdown body;
 *  2. the first product image from `productsSnapshot`.
 */
export function draftHeroImage(
  draft: Pick<ContentDraft, 'body' | 'productsSnapshot'> & { imageUrl?: string | null }
): string | undefined {
  if (draft.imageUrl) return draft.imageUrl;
  const body = draft.body ?? '';
  const match = body.match(/!\[[^\]]*\]\((https?:\/\/[^\s)]+)\)/);
  if (match) return match[1];
  return parseDraftProducts(draft.productsSnapshot).find(p => p.image_url)?.image_url;
}

/**
 * 一张稿子配图在 Hub 里怎么摆 —— 只定「怎么装进框」这一件事,尺寸由各处自己决定。
 *
 * [2026-09-16] 原来四处都是 `object-cover`:图被按框的比例**裁掉**多出来的部分。
 * Maya 的配图是按渠道比例出的(IG 多为 1:1 / 4:5),而这些框是又宽又矮的固定高度,
 * 于是商家在 Hub 里看到的是**一条腰**:2026-09-14 那张已发布的 Instagram 帖,两块
 * 竖着的滑板被上下各切掉一截,板头板尾都不在画面里。
 *
 * Hub 是**复核**的地方 —— 商家来这儿是确认「发出去的/要发的是这张图」,所以图必须
 * 完整。`object-contain` 按比例缩放塞进框,不裁;`bg-gray-50` 让留白看起来是设计,
 * 不是没加载出来。
 *
 * 只共用这一条决定,不共用尺寸:弹窗要 `max-h-*`(随图高,封顶),列表卡要固定
 * `h-*`(和旁边的正文块对齐)。把尺寸也塞进来会让两种版式互相将就。
 */
export const HERO_IMG_FIT = 'bg-gray-50 object-contain';

/** The cover entry's URL: role='cover' first, else the first entry. */
function coverMediaUrl(items: { image_url?: string; role?: string }[]): string | undefined {
  return (items.find(m => m.role === 'cover') ?? items[0])?.image_url || undefined;
}

/**
 * Map a persisted {@link ContentDraft} to the {@link ContentStudioDraft} shape
 * that {@link buildContentStudioPublishMessage} consumes, so publishing from the
 * Drafts tab produces the exact same "reviewed final content" message the
 * Content Studio editor sends. `contentType` blog → article (the message noun).
 */
export function toContentStudioDraft(draft: ContentDraft): ContentStudioDraft {
  return {
    content_type: draft.contentType === 'blog' ? 'article' : 'post',
    title: draft.title ?? '',
    content: draft.body ?? '',
    platform: draft.contentType === 'social' ? 'the connected social channels' : undefined,
    tags: [],
    // Carried so a publish from the Hub can send the DURABLE cover key rather than a
    // URL scraped out of the markdown body. Note what is NOT here: the persisted draft
    // has no summary or tags column, so an article published from the Hub goes out
    // without tags and without an SEO meta description.
    media: parseDraftMedia(draft.media),
    // Cover precedence, most-recently-authored first: an editor-replaced cover persisted as a
    // URL entry in `media` → the server-resolved `imageUrl` (the durable key's public URL, what
    // the Material Hub renders) → the body's first image → the product photo.
    image_url: coverMediaUrl(parseDraftMedia(draft.media)) ?? draftHeroImage(draft),
    source: 'tool',
  };
}
